import { ARTICLES } from '../articles';
import { ThemeId } from '../articles/types';

// Tous les outils, guides et articles du site vitrine, par thème : page /guides,
// pied de page, « À lire aussi ». Une page outil ou guide s'ajoute ici ; un
// article du blog s'ajoute dans ../articles et rejoint son thème tout seul.

export interface GuideEntry {
  to: string;
  label: string; // libellé court (pied de page)
  title: string; // titre de la carte sur /guides
  text: string;
  tool?: boolean; // outil interactif (sinon article)
  theme?: ThemeId;
}

export interface GuideTheme {
  id: ThemeId;
  title: string;
  text: string; // sous-titre sur /guides
  items: GuideEntry[];
}

// Pages piliers (guides et outils écrits à la main), par thème
const PILLARS: Record<ThemeId, GuideEntry[]> = {
  preparer: [
    {
      to: '/retroplanning-mariage',
      label: 'Rétroplanning mariage',
      title: 'Rétroplanning mariage, mois par mois',
      text: 'Quoi faire et quand, de 18 mois avant jusqu’au jour J.',
    },
    {
      to: '/liste-invites-mariage',
      label: 'Liste d’invités',
      title: 'Liste d’invités : modèle Excel gratuit',
      text: 'Le modèle à télécharger et la méthode pour arriver au bon nombre.',
    },
  ],
  budget: [
    {
      to: '/budget-mariage',
      label: 'Budget mariage',
      title: 'Budget mariage : prix moyen et répartition',
      text: 'Combien coûte un mariage, poste par poste et par invité.',
    },
  ],
  invitations: [
    {
      to: '/quand-envoyer-faire-part-mariage',
      label: 'Quand envoyer les faire-part',
      title: 'Quand envoyer les faire-part ?',
      text: 'Le bon moment pour les save the date, les faire-part et la date limite de réponse.',
    },
    {
      to: '/rsvp-mariage',
      label: 'RSVP mariage',
      title: 'RSVP mariage : le guide complet',
      text: 'Le RSVP en ligne, la date limite, les textes à copier et les relances.',
    },
    {
      to: '/qr-code-faire-part-mariage',
      label: 'QR code faire-part',
      title: 'Un QR code gratuit sur le faire-part',
      text: 'Le créer, le placer, et éviter les QR codes qui expirent.',
    },
    {
      to: '/formulaire-reponse-mariage',
      label: 'Formulaire de réponse',
      title: 'Le formulaire de réponse idéal',
      text: 'Les questions à poser à vos invités, un exemple, et le carton réponse.',
    },
    {
      to: '/google-form-mariage',
      label: 'Google Form mariage',
      title: 'Google Form pour le mariage',
      text: 'Le pas-à-pas, ses limites, et une alternative gratuite.',
    },
  ],
  textes: [],
  'plan-de-table': [
    {
      to: '/plan-de-table-mariage',
      label: 'Plan de table mariage',
      title: 'Plan de table mariage en ligne',
      text: 'Placez vos invités à la souris, à essayer directement sur la page.',
      tool: true,
    },
  ],
  'jour-j': [
    {
      to: '/calculateur-boissons-mariage',
      label: 'Calculateur de boissons',
      title: 'Calculateur de boissons',
      text: 'Combien de bouteilles de vin, de champagne et de rosé, moment par moment.',
      tool: true,
    },
  ],
};

const THEMES: Array<Omit<GuideTheme, 'items'>> = [
  { id: 'preparer', title: 'Préparer le mariage', text: 'Le planning, les démarches, la date et la saison.' },
  { id: 'budget', title: 'Budget et prix', text: 'Combien coûte chaque poste, et comment garder la main sur le total.' },
  { id: 'invitations', title: 'Faire-part et réponses', text: 'Inviter, en papier ou en ligne, et recevoir toutes les réponses.' },
  { id: 'textes', title: 'Textes et discours', text: 'Des textes à copier pour chaque étape, du save the date au merci.' },
  { id: 'plan-de-table', title: 'Plan de table', text: 'Placer tout le monde, et le montrer joliment le jour J.' },
  { id: 'jour-j', title: 'Réception et jour J', text: 'Le déroulé, le menu, les boissons et les animations.' },
];

export const GUIDE_THEMES: GuideTheme[] = THEMES.map((theme) => ({
  ...theme,
  items: [
    ...PILLARS[theme.id],
    ...ARTICLES.filter((a) => a.theme === theme.id).map((a) => ({
      to: `/${a.slug}`,
      label: a.label,
      title: a.title,
      text: a.card,
    })),
  ].map((item) => ({ ...item, theme: theme.id })),
}));

export const ALL_GUIDES = GUIDE_THEMES.flatMap((t) => t.items);
export const TOOL_PAGES = ALL_GUIDES.filter((g) => g.tool);
export const ARTICLE_PAGES = ALL_GUIDES.filter((g) => !g.tool);

// « À la une » du pied de page : les sujets les plus recherchés
export const FEATURED_PAGES = [
  '/texte-faire-part-mariage',
  '/budget-mariage',
  '/retroplanning-mariage',
  '/rsvp-mariage',
  '/site-mariage-gratuit',
  '/planning-jour-j-mariage',
].map((to) => ALL_GUIDES.find((g) => g.to === to)!);
